import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { DateTime } from "luxon";
import type { V3Player } from "@neuromotion-inc/mightier-ts-sdk";

interface PlayerNameDisplay {
  getPlayerNameDisplay: (player: V3Player) => string;
}

export const usePlayerNameDisplay = (): PlayerNameDisplay => {
  const { t } = useTranslation();

  const getPlayerNameDisplay = useCallback(
    (player: V3Player) => {
      const { name, username } = player;

      // name being falsey means that was created on game app and user has
      // not taken the player profile survey yet.
      if (!name || name === username) {
        return t("utils.playerDisplayName.single", { username });
      }

      return t("utils.playerDisplayName.double", { name, username });
    },
    [t],
  );

  return useMemo(() => {
    return { getPlayerNameDisplay };
  }, [getPlayerNameDisplay]);
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useFormatDateTime = () => {
  const { i18n } = useTranslation();

  return {
    formatDateTime: (
      dt: DateTime,
      fmt?: Intl.DateTimeFormatOptions,
    ): string => {
      const localizedDt = dt.setLocale(i18n.language);
      return localizedDt.toLocaleString(fmt);
    },
  };
};
